"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTheme } from "next-themes";
import {
  PRESETS,
  PRESETS_BY_SLUG,
  applyTheme,
  clearTheme,
  type BrandTheme,
  type ModeRequest,
  type ThemeOverrides,
} from "../brand-themes/index";

const COOKIE_NAME = "gz_theme";
const OVERRIDES_KEY = "gz_theme_overrides";

interface BrandThemeContextValue {
  activeTheme: BrandTheme | null;
  // Always "auto" — mode is owned by next-themes (the standalone ThemeToggle).
  // Kept on the context for backward compat with components that read it.
  mode: ModeRequest;
  overrides: ThemeOverrides;
  setTheme: (slug: string | null) => void;
  // Deprecated no-op. Mode control belongs to next-themes' setTheme().
  // Retained so existing callers compile; emits a console warning in dev.
  setMode: (mode: ModeRequest) => void;
  setVar: (name: string, value: string) => void;
  resetVar: (name: string) => void;
  resetAll: () => void;
  presets: BrandTheme[];
}

const Ctx = createContext<BrandThemeContextValue | null>(null);

// Cookie value is the slug. Older cookies were `slug:mode` — we still parse
// the slug out of those (taking the part before `:`) so existing cookies
// keep the brand selected. The mode component is ignored: next-themes owns
// light/dark globally, no per-brand override.
function parseCookieSlug(raw: string | null): string | null {
  if (!raw) return null;
  const [slug] = raw.split(":");
  return slug || null;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  const value = m?.[1];
  return value !== undefined ? decodeURIComponent(value) : null;
}

function writeCookie(name: string, value: string | null) {
  if (typeof document === "undefined") return;
  // Cookie domain restricted to *.l.gaz.codes — picker is local-only.
  const isLocal = window.location.hostname.endsWith(".l.gaz.codes");
  const domainAttr = isLocal ? "; Domain=.l.gaz.codes" : "";
  if (value === null) {
    document.cookie = `${name}=; Path=/; Max-Age=0${domainAttr}`;
  } else {
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=31536000; SameSite=Lax${domainAttr}`;
  }
}

export interface BrandThemeProviderProps {
  children: ReactNode;
  // Initial slug, typically read from cookie SSR-side and passed down so the
  // client doesn't have to re-read on hydration. Optional; if undefined we
  // fall back to the cookie at mount.
  initialSlug?: string | null;
  // Accepted for backward compat with existing callers but ignored — mode is
  // owned by next-themes now.
  initialMode?: ModeRequest;
}

export function BrandThemeProvider({ children, initialSlug }: BrandThemeProviderProps) {
  const [activeSlug, setActiveSlug] = useState<string | null>(() => initialSlug ?? null);
  const [overrides, setOverrides] = useState<ThemeOverrides>({});

  // Subscribe to next-themes' resolved theme. When it changes (system pref
  // flip, user clicked light/dark), re-run applyTheme so the brand's vars
  // resync to the right per-mode bucket. `resolvedTheme` is "light"|"dark"
  // even when the user's pref is "system" (next-themes resolves it for us).
  const { resolvedTheme } = useTheme();

  // On mount: if no initialSlug was provided, read cookie.
  useEffect(() => {
    if (initialSlug === undefined) {
      const slug = parseCookieSlug(readCookie(COOKIE_NAME));
      if (slug && PRESETS_BY_SLUG[slug]) {
        setActiveSlug(slug);
      }
    }
    try {
      const raw = localStorage.getItem(OVERRIDES_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { slug: string; vars: ThemeOverrides };
        const cookieSlug = parseCookieSlug(readCookie(COOKIE_NAME));
        if (parsed.slug && parsed.slug === (initialSlug ?? cookieSlug)) {
          setOverrides(parsed.vars ?? {});
        }
      }
    } catch {
      /* ignore localStorage parse errors */
    }
  }, [initialSlug]);

  const activeTheme = activeSlug ? PRESETS_BY_SLUG[activeSlug] ?? null : null;

  // Apply theme on every change AND when next-themes flips. We pass
  // resolvedTheme as the explicit mode rather than letting applyTheme read
  // `<html class>` itself — passing it explicitly (a) makes resolvedTheme
  // a real dependency (linter happy), and (b) avoids a race where the
  // document class hasn't updated yet when our effect runs.
  //
  // resolvedTheme is "light"|"dark" once mounted; undefined during SSR /
  // pre-hydration. When undefined we fall back to "auto" so applyTheme can
  // try to read the class itself or default to defaultMode.
  useEffect(() => {
    if (activeTheme) {
      const mode: ModeRequest =
        resolvedTheme === "light" || resolvedTheme === "dark" ? resolvedTheme : "auto";
      applyTheme(activeTheme, overrides, { mode });
    } else {
      clearTheme();
    }
  }, [activeTheme, overrides, resolvedTheme]);

  // Storage helpers.
  //
  // `typeof localStorage !== "undefined"` only guards SSR — it does NOT make the
  // call safe. In Safari private browsing (and when the quota is exhausted, or
  // storage is disabled by policy) the object EXISTS and the call THROWS. These
  // sites were unguarded, so a theme change threw and took the render with it,
  // over a preference the app can happily do without. tdd-0075.
  const storageRemove = useCallback((key: string) => {
    if (typeof localStorage === "undefined") return;
    try {
      localStorage.removeItem(key);
    } catch {
      /* private browsing / quota / disabled — the override just doesn't persist */
    }
  }, []);

  const storageWrite = useCallback((key: string, value: string) => {
    if (typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(key, value);
    } catch {
      /* as above — non-fatal by design */
    }
  }, []);

  const setTheme = useCallback((slug: string | null) => {
    setActiveSlug(slug);
    setOverrides({});
    writeCookie(COOKIE_NAME, slug);
    storageRemove(OVERRIDES_KEY);
  }, [storageRemove]);

  const setMode = useCallback((_next: ModeRequest) => {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "[useBrandTheme] setMode() is a no-op — mode is owned by next-themes. Use useTheme().setTheme() instead.",
      );
    }
  }, []);

  // Persist the overrides map, or clear the key when it empties.
  const persistOverrides = useCallback(
    (next: ThemeOverrides) => {
      if (!activeSlug) return;
      if (Object.keys(next).length === 0) {
        storageRemove(OVERRIDES_KEY);
      } else {
        storageWrite(OVERRIDES_KEY, JSON.stringify({ slug: activeSlug, vars: next }));
      }
    },
    [activeSlug, storageRemove, storageWrite],
  );

  // The storage write happens OUTSIDE the setState updater.
  //
  // A setState callback must be pure — React may invoke it more than once for a
  // single update (StrictMode double-invokes it, and a discarded render can too).
  // Writing to localStorage from inside it made persistence a side effect of
  // render bookkeeping. Computing `next` from the current `overrides` keeps the
  // write on the event path, where it happens exactly once. tdd-0075.
  const setVar = useCallback(
    (name: string, value: string) => {
      const next = { ...overrides, [name]: value };
      setOverrides(next);
      persistOverrides(next);
    },
    [overrides, persistOverrides],
  );

  const resetVar = useCallback(
    (name: string) => {
      const next = { ...overrides };
      delete next[name];
      setOverrides(next);
      persistOverrides(next);
    },
    [overrides, persistOverrides],
  );

  const resetAll = useCallback(() => {
    setOverrides({});
    storageRemove(OVERRIDES_KEY);
  }, [storageRemove]);

  const value = useMemo<BrandThemeContextValue>(
    () => ({
      activeTheme,
      mode: "auto",
      overrides,
      setTheme,
      setMode,
      setVar,
      resetVar,
      resetAll,
      presets: PRESETS,
    }),
    [activeTheme, overrides, setTheme, setMode, setVar, resetVar, resetAll],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBrandTheme(): BrandThemeContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Provider not mounted — return a safe inert default. This lets components
    // call useBrandTheme() without crashing in apps that haven't wired up
    // the provider yet.
    return {
      activeTheme: null,
      mode: "auto",
      overrides: {},
      setTheme: () => {},
      setMode: () => {},
      setVar: () => {},
      resetVar: () => {},
      resetAll: () => {},
      presets: PRESETS,
    };
  }
  return ctx;
}
